import { makeStyles, tokens } from '@fluentui/react-components';
import { useState } from 'react';

import {
  ErrorState,
  LoadingState,
  PageHeader,
} from '@/design-system';
import { useDashboardHealth } from '@/lib/queries/dashboard';
import type { ResourceTimeRange } from '@/models/resourceDetail';
import { spacing } from '@/theme/tokens';

import { useResourceDetail } from './api';
import { ResourceDetailPanel } from './components/ResourceDetailPanel';
import { ResourceList } from './components/ResourceList';
import { TimeRangePicker } from './components/TimeRangePicker';

const useStyles = makeStyles({
  layout: {
    display: 'grid',
    gridTemplateColumns: 'minmax(260px, 320px) minmax(0, 1fr)',
    gap: spacing.xl,
    alignItems: 'start',
    '@media (max-width: 960px)': {
      gridTemplateColumns: '1fr',
    },
  },
  toolbar: {
    display: 'flex',
    alignItems: 'center',
    paddingBottom: spacing.l,
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
    marginBottom: spacing.l,
    gap: spacing.l,
    flexWrap: 'wrap',
  },
  toolbarPicker: {
    marginLeft: 'auto',
  },
});

export function ResourcesPage() {
  const classes = useStyles();
  const [explicitId, setExplicitId] = useState<string | null>(null);
  const [range, setRange] = useState<ResourceTimeRange>('24h');

  const dashboard = useDashboardHealth();

  // Derive the active selection on render rather than syncing state in an
  // effect (react-hooks/set-state-in-effect). Validate `explicitId` against
  // the current catalog so a stale value left over from a previous env (or a
  // resource that was just removed) doesn't drive a 404 in the detail panel —
  // fall back to the first resource in that case.
  const resources = dashboard.data?.resources ?? [];
  const firstId = resources[0]?.id ?? null;
  const explicitIdInList = explicitId !== null && resources.some((r) => r.id === explicitId);
  const selectedId = explicitIdInList ? explicitId : firstId;
  const detail = useResourceDetail({ resourceId: selectedId, range });

  return (
    <div>
      <PageHeader
        title="Resource health"
        description="Per-resource uptime, latency, error rate, and provider-specific metrics. Pick a resource on the left."
      />

      <div className={classes.toolbar}>
        <div className={classes.toolbarPicker}>
          <TimeRangePicker value={range} onChange={setRange} />
        </div>
      </div>

      {dashboard.isLoading ? (
        <LoadingState label="Loading resources…" />
      ) : dashboard.error ? (
        <ErrorState
          title="Couldn't load resources"
          description={dashboard.error.message}
          retry={() => {
            void dashboard.refetch();
          }}
        />
      ) : (
        <div className={classes.layout}>
          <ResourceList
            resources={dashboard.data?.resources ?? []}
            selectedId={selectedId}
            onSelect={setExplicitId}
          />
          <ResourceDetailPanel
            detail={detail.data}
            loading={detail.isLoading}
            isRefreshing={detail.isFetching && !detail.isLoading}
            error={detail.error}
            onRetry={() => {
              void detail.refetch();
            }}
          />
        </div>
      )}
    </div>
  );
}
